import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ConfirmationDialog from '@sayyesschool/frontend/components/confirmation-dialog';
import EditableText from '@sayyesschool/frontend/components/editable-text';
import ImageSection from '@sayyesschool/frontend/components/image-section';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import { getImagePath, useLesson } from '@sayyesschool/frontend/features/courses';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';
import { Flex, Grid } from '@sayyesschool/frontend/ui-components';

import LessonDescription from '../lesson-description';
import LessonSections from '../lesson-sections';

export default function LessonPage() {
    const navigate = useNavigate();
    const params = useParams();
    const { course, unit, lesson, actions } = useLesson(params);

    const [isConfirmationDialogOpen, toggleConfirmationDialogOpen] = useBoolean(false);

    const handleUpdateLesson = useCallback(data => {
        return actions.updateLesson(course.id, lesson.id, data);
    }, [course, lesson, actions]);

    const handleUpdateLessonTitle = useCallback(title => {
        return actions.updateLesson(course.id, lesson.id, { title });
    }, [course, lesson, actions]);

    const handleUpdateLessonImage = useCallback(image => {
        return actions.updateLesson(course.id, lesson.id, { image });
    }, [course, lesson, actions]);

    const handleDeleteLesson = useCallback(() => {
        return actions.deleteLesson(course.id, lesson.id)
            .then(() => navigate(unit.url));
    }, [course, unit, lesson, actions]);

    const handleCreateExercise = useCallback(data => {
        data.unitId = unit.id;
        data.lessonId = lesson.id;

        return actions.createExercise(course.id, data);
    }, [course, unit, lesson, actions]);

    const handleDeleteExercise = useCallback(exercise => {
        return actions.deleteExercise(course.id, exercise.id);
    }, [course, actions]);

    const handleCreateSection = useCallback(data => {
        data.unitId = unit.id;
        data.lessonId = lesson.id;

        return actions.createSection(course.id, data);
    }, [course, unit, lesson, actions]);

    const handleDeleteSection = useCallback(section => {
        return actions.deleteSection(course.id, section.id);
    }, [course, actions]);

    if (!lesson) return <LoadingIndicator fullscreen />;

    return (
        <Page className="LessonPage">
            <Page.Header
                title={
                    <EditableText
                        content={lesson.title}
                        required
                        onUpdate={handleUpdateLessonTitle}
                    />
                }
                breadcrumbs={[
                    { key: 'course', content: course.title, to: course.uri, title: 'Курс' },
                    { key: 'unit', content: unit.title, to: unit.uri, title: 'Юнит' }
                ]}
                actions={[
                    {
                        key: 'delete',
                        icon: 'delete',
                        title: 'Удалить урок',
                        onClick: toggleConfirmationDialogOpen
                    }
                ]}
            />

            <Page.Content>
                <Grid spacing={2}>
                    <Grid.Item xs={8}>
                        <LessonDescription
                            lesson={lesson}
                            onUpdate={handleUpdateLesson}
                        />
                    </Grid.Item>

                    <Grid.Item xs={4}>
                        <Flex
                            spacing={2}
                            column
                        >
                            <ImageSection
                                image={lesson.image}
                                path={getImagePath(course.id, lesson.id)}
                                cropperOptions={{ aspectRatio: 16 / 9 }}
                                onChange={handleUpdateLessonImage}
                            />

                            <LessonSections
                                lesson={lesson}
                                onCreate={handleCreateSection}
                                onDelete={handleDeleteSection}
                                onReorder={handleUpdateLesson}
                            />
                        </Flex>
                    </Grid.Item>
                </Grid>
            </Page.Content>

            <ConfirmationDialog
                title="Удалить урок?"
                message={`Урок "${lesson.title}" будет удален без возможности восстановления.`}
                open={isConfirmationDialogOpen}
                onConfirm={handleDeleteLesson}
                onClose={toggleConfirmationDialogOpen}
            />
        </Page>
    );
}